import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import RadioChecked from '@/images/RadioChecked.svg';
import RadioUnChecked from '@/images/RadioUnChecked.svg';
import { SwitchKnowledgeBaseSourceModal } from '@/pages/agents/components/modals';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { AgentKnowledgeSource } from '@d-id/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { New } from '../../common/banners/new';

export const RadioContainer = styled.div`
    display: flex;
    align-items: center;
    flex: 1 0 0;

    box-sizing: content-box;
    height: 28px;

    padding: 12px;
    gap: 8px;

    border-radius: 8px;
    border: 1px solid ${({ checked }: { checked: boolean }) => (checked ? theme.colors.secondary.main : theme.colors.secondary[200])};

    cursor: pointer;
`;

export const Name = styled.div<{ disabled?: boolean }>`
    color: ${({ disabled }: { disabled?: boolean }) => (disabled ? theme.colors.secondary[400] : 'black')};
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
`;

const iconSize = '16px';
export const Radio = ({ checked, disabled }: { checked: boolean; disabled?: boolean }) => {
    return (
        <MaskedIcon
            src={checked ? RadioChecked : RadioUnChecked}
            size={iconSize}
            color={disabled ? theme.colors.secondary[400] : theme.colors.secondary.main}
        />
    );
};

interface Props {
    knowledgeSource: AgentKnowledgeSource;
    displayName: string;
    disabled?: boolean;
}

export const KnowledgeSource = observer(({ knowledgeSource, displayName, disabled }: Props) => {
    const agentEditorStore = useAgentEditorStore();

    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

    const lastKnowledgeSourceHasInput = useMemo(() => {
        if (knowledgeSource === AgentKnowledgeSource.BaseKnowledge) {
            return agentEditorStore.uploadingDocumentFiles.length > 0;
        } else if (knowledgeSource === AgentKnowledgeSource.Documents) {
            return (agentEditorStore.baseKnowledge?.length ?? 0) > 0;
        } else {
            return false;
        }
    }, [agentEditorStore.baseKnowledge, agentEditorStore.uploadingDocumentFiles, knowledgeSource]);

    const onModalClose = useCallback(() => setIsConfirmationModalOpen(false), []);

    const onUpdate = useCallback(
        (value: AgentKnowledgeSource) => () => {
            agentEditorStore.setLLM(prev => ({
                ...prev,
                prompt_customization: { ...prev.prompt_customization, knowledge_source: value },
            }));
            onModalClose();
        },
        [onModalClose]
    );

    const onClick = useCallback(() => {
        if (disabled) return;

        return lastKnowledgeSourceHasInput ? setIsConfirmationModalOpen(true) : onUpdate(knowledgeSource)();
    }, [lastKnowledgeSourceHasInput, onUpdate, disabled]);

    const isChecked = useMemo(
        () =>
            agentEditorStore.llm.prompt_customization?.knowledge_source === knowledgeSource ||
            (!agentEditorStore.llm.prompt_customization?.knowledge_source && knowledgeSource === agentEditorStore.defaultKnowledgeSource),
        [agentEditorStore.llm.prompt_customization?.knowledge_source, knowledgeSource]
    );

    return (
        <>
            <RadioContainer checked={isChecked} onClick={onClick}>
                <Radio checked={isChecked} disabled={disabled} />
                <Name disabled={disabled}>
                    {displayName} {knowledgeSource === AgentKnowledgeSource.BaseKnowledge && <New />}
                </Name>
            </RadioContainer>
            <SwitchKnowledgeBaseSourceModal isOpen={isConfirmationModalOpen} onClose={onModalClose} onSave={onUpdate(knowledgeSource)} />
        </>
    );
});
